import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate, useParams } from "@tanstack/react-router";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { AddDocumentsDialog } from "@/components/AddDocumentsDialog.tsx";
import { DocumentDrawer } from "@/components/DocumentDrawer.tsx";
import { TopBar } from "@/components/TopBar.tsx";
import { UploadPanel } from "@/components/UploadPanel.tsx";
import { Button, EmptyState, Pill } from "@/components/primitives.tsx";
import { formatCount } from "@/i18n/index.ts";
import { api } from "@/lib/api.ts";
import { parseLegalTitle, truncateAtWord } from "@/lib/legalTitle.ts";
import type { CorpusDocument } from "@/lib/api-types.ts";

/** One collection, and the documents labelled with it. */
export function CollectionDetailRoute() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { collectionId } = useParams({ from: "/collections/$collectionId" });
  const queryClient = useQueryClient();

  const collections = useQuery({ queryKey: ["collections"], queryFn: api.collections });
  const collection = collections.data?.find((item) => item.id === collectionId);

  // The row the reader clicked. Held here rather than in the drawer so the table stays
  // the single source of what is on screen.
  const [openDocument, setOpenDocument] = useState<CorpusDocument | null>(null);
  const [adding, setAdding] = useState(false);

  const documents = useQuery({
    queryKey: ["corpus", "collection", collectionId],
    queryFn: () => api.corpus({ collection_id: collectionId, limit: 200 }),
  });

  // Both the upload and the filing land in the SAME two lists, so both refresh the same
  // pair. Invalidating only the collection's own page would leave Pustaka and the card
  // counts showing the state before the change.
  function refresh() {
    void queryClient.invalidateQueries({ queryKey: ["corpus"] });
    void queryClient.invalidateQueries({ queryKey: ["collections"] });
  }

  return (
    <div className="lib">
      <TopBar />
      <div className="wrap">
        <Link to="/collections" className="btn btn-ghost btn-sm">
          ← {t("collections.title")}
        </Link>

        <div className="row" style={{ margin: "var(--s4) 0 var(--s2)" }}>
          <div>
            <h1 className="page-title">{collection?.name ?? t("common.loading")}</h1>
            {collection?.description ? <p className="page-sub">{collection.description}</p> : null}
          </div>
          <span className="spacer" />
          <Button onClick={() => setAdding(true)}>{t("collection.addDocuments")}</Button>
          <Button
            variant="primary"
            onClick={() =>
              // Starting a conversation here seeds the scope with this collection AND
              // keeps Pustaka on, so "does this conflict with existing regulation?"
              // works without being configured first.
              void navigate({ to: "/studio", search: { collection: collectionId } })
            }
          >
            {t("nav.studio")}
          </Button>
        </div>

        <div className="row" style={{ marginBottom: "var(--s4)", flexWrap: "wrap" }}>
          {collection?.confidential ? <Pill tone="amber">{t("collections.confidential")}</Pill> : null}
          {collection?.owning_org_unit ? <Pill>{collection.owning_org_unit}</Pill> : null}
          {collection?.min_position_level !== null && collection?.min_position_level !== undefined ? (
            <Pill>
              {t("collections.positionLevel")}: {collection.min_position_level}
            </Pill>
          ) : null}
        </div>

        {/* UPLOAD HAPPENS HERE, pinned to this collection. It used to be a link to
            Pustaka that carried no collection id, so the commonest way to fill a
            collection was the one way that could not fill it. */}
        {collection ? (
          <div style={{ marginBottom: "var(--s5)" }}>
            <UploadPanel
              collections={collections.data ?? []}
              fixedCollection={{ id: collection.id, name: collection.name }}
              onUploaded={refresh}
            />
          </div>
        ) : null}

        <div className="eyebrow">{t("collection.documents")}</div>
        {/* The two "off" switches must never read the same. This page's exclusion is
            global; the rail's per-document ticks narrow only the open conversation. */}
        <p className="hint" style={{ margin: "4px 0 var(--s3)" }}>
          {t("collection.excludeHelp")}
        </p>

        {documents.data && documents.data.documents.length === 0 ? (
          <EmptyState
            title={t("collection.emptyTitle")}
            body={t("collection.emptyBody")}
            action={
              // Was a link to Pustaka, which dropped the collection on the way and is
              // exactly how documents ended up filed nowhere. The two ways in are both
              // on this page now: upload above, or file something that already exists.
              <Button variant="primary" onClick={() => setAdding(true)}>
                {t("collection.addDocuments")}
              </Button>
            }
          />
        ) : null}

        <div className="grid-wrap">
          {/* FIXED layout with declared widths. On `auto`, one 300-character ALL-CAPS
              legal title stretched the first column past the viewport and the header row
              drifted ~200px away from the cells beneath it — the columns looked unrelated
              to their headings. */}
          <table className="doc-table">
            <thead>
              <tr>
                <th style={cell}>{t("library.columns.title")}</th>
                <th style={{ ...cell, width: 130 }}>{t("library.columns.source")}</th>
                <th style={{ ...cell, width: 80 }}>{t("library.columns.format")}</th>
                <th style={{ ...cell, width: 120 }}>{t("library.columns.status")}</th>
              </tr>
            </thead>
            <tbody>
              {documents.data?.documents.map((document) => (
                /* THE WHOLE ROW OPENS THE DOCUMENT. A table of titles with nothing to
                   click is the shape that makes a reader think the upload did not work —
                   they have no way to check the file they just added. */
                <tr
                  key={document.id}
                  className="doc-table-row"
                  onClick={() => setOpenDocument(document)}
                  tabIndex={0}
                  role="button"
                  aria-label={document.title}
                  onKeyDown={(event) => {
                    if (event.key === "Enter" || event.key === " ") {
                      event.preventDefault();
                      setOpenDocument(document);
                    }
                  }}
                >
                  {/* Label + subject, title-cased and truncated — the same treatment the
                      source picker gives. 43% of these titles are ALL-CAPS and two thirds
                      run past 120 characters; printed raw they are a wall nobody reads.
                      The full string stays in `title=` and is what citations quote. */}
                  <td style={cell} title={parseLegalTitle(document.title).full}>
                    <span className="doc-cell">
                      {parseLegalTitle(document.title).label ? (
                        <span className="mono doc-cell-label">
                          {parseLegalTitle(document.title).label}
                        </span>
                      ) : null}
                      <span>{truncateAtWord(parseLegalTitle(document.title).subject, 96)}</span>
                    </span>
                  </td>
                  <td style={cell}>
                    <Pill>{document.source_type}</Pill>
                  </td>
                  <td style={{ ...cell, fontFamily: "var(--mono)", fontSize: 12 }}>
                    {document.format ?? "—"}
                  </td>
                  <td style={cell}>
                    <Pill tone={document.status === "indexed" ? "green" : document.status === "failed" ? "red" : "amber"}>
                      {t(`library.status.${document.status}`)}
                    </Pill>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {documents.data ? (
          <p className="hint" style={{ marginTop: "var(--s3)" }}>
            {t("library.total", {
              shown: formatCount(documents.data.documents.length, i18n.language),
              total: formatCount(documents.data.total, i18n.language),
            })}
          </p>
        ) : null}
      </div>

      <DocumentDrawer
        document={openDocument}
        collections={collections.data ?? []}
        onChanged={refresh}
        onClose={() => setOpenDocument(null)}
      />

      {adding && collection ? (
        <AddDocumentsDialog
          collectionId={collection.id}
          collectionName={collection.name}
          onClose={() => setAdding(false)}
          onAdded={refresh}
        />
      ) : null}
    </div>
  );
}

const cell: React.CSSProperties = {
  textAlign: "left",
  padding: "9px 12px",
  borderBottom: "1px solid var(--line-soft)",
  fontSize: 13.5,
};
